import React, { useState, useEffect, useRef } from 'react';
import { 
  Wallet, Zap, TrendingUp, Eye, EyeOff, ArrowUpRight, ArrowDownRight,
  Send, BrainCircuit, DollarSign, Shield, AlertTriangle, LineChart,
  Bell, Settings, Target, Lock, ChartBar, Users, Clock, Cloud, BarChart3, Upload, FileSpreadsheet, FileText, Brain, Calculator, Check, ShoppingCart, Star, X
} from 'lucide-react';
import { LineChart as Chart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useDarkMode } from '../context/DarkModeContext';
import FeatureDemo from '../components/FeatureDemo';
import Footer from '../components/Footer';
import alertSound from '../assets/audio/alert.mp3';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import confetti from 'canvas-confetti';
import BankingDemo from '../components/BankingDemo';
import AIChatBot from '../components/AIChatBot';

// Mock data
const spendingData = [
  { month: 'Jan', expenses: 2400, savings: 1400, investments: 800 },
  { month: 'Feb', expenses: 2100, savings: 1600, investments: 900 },
  { month: 'Mar', expenses: 2300, savings: 1500, investments: 1000 },
  { month: 'Apr', expenses: 2000, savings: 1800, investments: 1100 },
  { month: 'May', expenses: 1900, savings: 2000, investments: 1300 },
  { month: 'Jun', expenses: 1850, savings: 2200, investments: 1400 },
];

const stockRecommendations = [
  {
    symbol: 'TSLA',
    name: 'Tesla Inc.',
    change: -5.2,
    recommendation: 'Consider buying on this dip',
    price: 175.25
  },
  {
    symbol: 'AAPL',
    name: 'Apple Inc.',
    change: 2.1,
    recommendation: 'Strong fundamentals, hold position',
    price: 172.40
  },
  {
    symbol: 'NVDA',
    name: 'NVIDIA Corp.',
    change: 4.5,
    recommendation: 'AI growth potential remains strong',
    price: 875.35
  }
];

const goals = [
  {
    name: 'Emergency Fund',
    current: 8500,
    target: 10000,
    progress: 85
  },
  {
    name: 'Vacation Savings',
    current: 3000,
    target: 5000,
    progress: 60
  },
  {
    name: 'New Car',
    current: 15000,
    target: 25000,
    progress: 60
  }
];

const alerts = [
  {
    type: 'warning',
    message: 'Large transaction detected: $500 at Electronics Store',
    timestamp: '2 hours ago'
  },
  {
    type: 'opportunity',
    message: 'TSLA is down 5% - good buying opportunity',
    timestamp: '4 hours ago'
  }
];

// Import any assets you might be using
const FEATURE_ASSETS = {
  // Add any image URLs or other assets here if needed
  // example: featureImage: new URL('../assets/feature.png', import.meta.url).href,
};

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: "easeOut"
    }
  }),
  hover: {
    y: -10,
    scale: 1.02,
    transition: {
      duration: 0.2,
      ease: "easeInOut"
    }
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: 0.1
    }
  }
};

const iconVariants = {
  initial: { scale: 1 },
  hover: { 
    scale: 1.2,
    rotate: 360,
    transition: {
      duration: 0.6,
      ease: "easeInOut"
    }
  }
};

const titleVariants = {
  initial: { opacity: 0, y: -20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const Features = () => {
  const { darkMode } = useDarkMode();
  const [showDemo, setShowDemo] = useState<string | null>(null);
  const [demoResponse, setDemoResponse] = useState<string>('');
  const [notifications, setNotifications] = useState<Array<{ id: number; message: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [analysisResult, setAnalysisResult] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeFeature, setActiveFeature] = useState('banking');
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [showPurchaseAlert, setShowPurchaseAlert] = useState(true);

  const alternatives = [
    {
      type: 'Investment',
      description: 'Tech Growth Fund - Expected 12% annual return',
      potential: '+$35.88 in 12 months'
    },
    {
      type: 'Savings',
      description: 'High-Yield Savings Account - 4.5% APY',
      potential: '+$13.45 in 12 months'
    }
  ];

  const playNotificationSound = () => {
    const audio = new Audio(alertSound);
    audio.play();
  };

  const addNotification = (message: string) => {
    const newNotification = {
      id: Date.now(),
      message
    };
    setNotifications(prev => [newNotification, ...prev]);
    playNotificationSound();
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
    }, 3000);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files) return;
    
    setIsLoading(true);
    const newFiles = Array.from(files);
    setUploadedFiles(prev => [...prev, ...newFiles]);

    try {
      const results = await Promise.all(
        newFiles.map(file => analyzeFinancialFile(file))
      );

      const analysis = results.join('\n\n');
      setDemoResponse(`
        Cloud Sync Complete ☁️

        Files Analyzed:
        ${newFiles.map(f => `✅ ${f.name}`).join('\n')}

        Quick Analysis:
        ${analysis}

        Recommendations:
        💡 Based on your spending patterns, we suggest:
        - Increase savings by $300/month
        - Review subscription services
        - Consider debt consolidation

        Data is now synced and will be used for:
        📊 Personalized budgeting
        📈 Investment suggestions
        🎯 Goal tracking
      `);
    } catch (error) {
      setDemoResponse(`
        ❌ Error analyzing files
        Please ensure files are in CSV, XLS, or XLSX format
        and contain financial data.
      `);
    }

    setIsLoading(false);
  };

  const analyzeFinancialFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const content = e.target?.result;
          let data;

          if (file.name.endsWith('.csv')) {
            data = Papa.parse(content as string, { header: true }).data;
          } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
            const workbook = XLSX.read(content, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
          }

          // Basic financial analysis
          const transactions = data as any[];
          const total = transactions.reduce((sum, row) => sum + (parseFloat(row.Amount) || 0), 0);
          const categories = [...new Set(transactions.map(t => t.Category))];
          
          resolve(`
            Total Transactions: ${transactions.length}
            Net Flow: $${total.toFixed(2)}
            Categories Found: ${categories.length}
          `);
        } catch (error) {
          reject(error);
        }
      };

      if (file.name.endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  const runConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  const animateProgress = () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 50);
  };

  const handleDemoAction = async (action: string) => {
    setIsLoading(true);
    
    switch (action) {
      case 'protection':
        animateProgress();
        setTimeout(() => {
          runConfetti();
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 2000);
          setDemoResponse(`
            🛡️ Security Scan Complete!

            ${Array(5).fill('').map(() => '🔒 Scanning...').join('\n')}
            
            Results:
            ✨ Account Security: Enhanced
            🔐 Encryption: Military-grade
            👁️ Monitoring: 24/7 Active
            
            Live Protection Stats:
            ${Array(3).fill('').map(() => '⚡ Blocking threats...').join('\n')}
            
            Status: Your account is fortress-strong! 
          `);
          setIsLoading(false);
        }, 3000);
        break;

      case 'investment':
        setTimeout(() => {
          setDemoResponse(`
            📊 Investment Analysis Report

            Risk Profile: Moderate Growth
            Time Horizon: 5-10 years
            Investment Goal: $500,000

            Recommended Portfolio:
            🔹 US Stocks: 45%
            🔹 International: 25%
            🔹 Bonds: 20%
            🔹 Real Estate: 7%
            🔹 Cash: 3%

            Projected Returns:
            📈 Conservative: 6.2%
            📈 Moderate: 8.5%
            📈 Aggressive: 10.8%

            Smart Recommendations:
            💡 Increase tech sector exposure
            💡 Consider ESG investments
            💡 Rebalance quarterly
          `);
          setIsLoading(false);
        }, 3000);
        break;

      case 'decisions':
        setTimeout(() => {
          setDemoResponse(`
            🤖 AI Decision Analysis

            Current Financial Health: Strong
            Monthly Savings: On Track
            Debt Management: Excellent

            Recent Decisions Analyzed:
            ✅ Home Purchase Planning
            ✅ Investment Strategy
            ✅ Emergency Fund Setup

            AI Recommendations:
            1️⃣ Optimal Mortgage Rate: 3.75%
            2️⃣ Suggested Down Payment: $60,000
            3️⃣ Emergency Fund Target: $25,000

            Action Items:
            🎯 Start house hunting in 3 months
            🎯 Increase 401(k) contribution by 2%
            🎯 Review insurance coverage
          `);
          setIsLoading(false);
        }, 2800);
        break;

      case 'budget':
        setTimeout(() => {
          setDemoResponse(`
            💰 Budget Optimization Report

            Monthly Overview:
            📥 Income: $6,500
            📤 Expenses: $4,800
            💫 Savings: $1,700

            Category Analysis:
            🏠 Housing: $2,000 (On target)
            🚗 Transport: $400 (High)
            🍽️ Food: $800 (High)
            🎮 Entertainment: $300 (Good)
            
            AI-Suggested Savings:
            🚗 Carpool to work: +$150/mo
            🍽️ Meal prep plan: +$200/mo
            📱 Switch carriers: +$45/mo
            
            Potential Monthly Savings: $395
            Annual Impact: $4,740 extra savings!
          `);
          setIsLoading(false);
        }, 2600);
        break;

      case 'notifications':
        playNotificationSound();
        setTimeout(() => {
          setDemoResponse(`
            🔔 Alert System Test Results

            Notification Channels:
            📱 Mobile Push: Success
            ✉️ Email: Success
            💬 SMS: Success
            🔊 Sound Alert: Active

            Sample Alerts:
            💳 Large Transaction: $1,000+
            📈 Stock Alert: AAPL +5%
            💰 Low Balance: Below $500
            🏦 Bill Due: 3 days notice

            Custom Preferences Set:
            - Market updates: Daily
            - Balance alerts: Real-time
            - Bill reminders: 3 days prior
            
            Click below to test sound again!
          `);
          setIsLoading(false);
        }, 2000);
        break;

      case 'cloud':
        setTimeout(() => {
          fileInputRef.current?.click();
          setDemoResponse(`
            Cloud Analysis Ready ☁️

            Supported Formats:
            📑 Bank Statements (CSV)
            📊 Investment Reports (PDF)
            📈 Trading History (XLSX)
            📋 Budget Spreadsheets

            Upload your financial documents for:
            - Automated Analysis
            - Pattern Recognition
            - Custom Insights
            - Trend Detection
          `);
          setIsLoading(false);
        }, 1000);
        break;

      case 'team':
        setTimeout(() => {
          setDemoResponse(`
            Team Workspace Created 👥

            Access Granted:
            👤 Financial Advisor
            👤 Tax Specialist
            👤 Investment Manager
            
            Features Enabled:
            💬 Secure Chat
            📁 Document Sharing
            📊 Real-time Collaboration
            📅 Meeting Scheduler

            Workspace ID: TM-${Math.random().toString(36).substr(2, 9)}
          `);
          setIsLoading(false);
        }, 1800);
        break;

      case 'automation':
        setTimeout(() => {
          setDemoResponse(`
            Smart Automation Active ⚡

            Automated Tasks:
            🔄 Bill Payments
            💰 Savings Transfers
            📊 Investment Rebalancing
            📈 Performance Tracking

            Next Scheduled Actions:
            - Rent Payment: 1st of month
            - Investment Review: 15th
            - Budget Analysis: End of month
            
            Status: All Systems Operational
          `);
          setIsLoading(false);
        }, 1500);
        break;

      case 'market':
        setTimeout(() => {
          setDemoResponse(`
            Live Market Analysis 📊

            Market Indicators:
            📈 S&P 500: 4,782.45 (+0.8%)
            💰 BTC/USD: $43,256 (+1.8%)
            📉 NASDAQ: 15,123 (-0.2%)
            
            Sector Performance:
            🔹 Technology: +2.3%
            🔹 Healthcare: +1.7%
            🔹 Finance: +0.9%

            AI Predictions:
            - Bullish tech sector trend
            - Potential rate change impact
            - Emerging market opportunity
          `);
          setIsLoading(false);
        }, 2000);
        break;
    }
  };

  const features = [
    {
      id: 'banking',
      title: 'Smart Banking',
      description: 'Manage your finances with AI-powered insights',
      icon: <Wallet className="w-6 h-6" />
    },
    {
      id: 'ai',
      title: 'AI Assistant',
      description: 'Get real-time financial advice',
      icon: <Shield className="w-6 h-6" />
    },
    {
      id: 'protection',
      icon: <Shield className="w-8 h-8 text-emerald-600" />,
      title: "Smart Financial Protection",
      description: "Our AI-powered system actively monitors your spending patterns and protects you from potential financial risks and fraud.",
      color: "bg-emerald-50/50",
      hoverColor: "hover:bg-emerald-50",
      textColor: "text-emerald-600",
      borderColor: "border-emerald-100",
      demoPrompt: "Would you like to run a security scan?",
      demoAction: () => {
        setIsLoading(true);
        animateProgress();
        setTimeout(() => {
          runConfetti();
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 2000);
          setDemoResponse(`
            🛡️ Security Scan Complete!

            ${Array(5).fill('').map(() => '🔒 Scanning...').join('\n')}
            
            Results:
            ✨ Account Security: Enhanced
            🔐 Encryption: Military-grade
            👁️ Monitoring: 24/7 Active
            
            Live Protection Stats:
            ${Array(3).fill('').map(() => '⚡ Blocking threats...').join('\n')}
            
            Status: Your account is fortress-strong! 
          `);
          setIsLoading(false);
        }, 3000);
      }
    },
    {
      id: 'investment',
      icon: <BarChart3 className="w-8 h-8 text-blue-600" />,
      title: "Intelligent Investment Insights",
      description: "Get personalized investment recommendations based on your financial goals, risk tolerance, and market analysis.",
      color: "bg-blue-50/50",
      hoverColor: "hover:bg-blue-50",
      textColor: "text-blue-600",
      borderColor: "border-blue-100",
      demoPrompt: "Generate personalized investment insights?",
      demoAction: () => {
        setIsLoading(true);
        setTimeout(() => {
          setDemoResponse(`
            📊 Investment Analysis Report

            Risk Profile: Moderate Growth
            Time Horizon: 5-10 years
            Investment Goal: $500,000

            Recommended Portfolio:
            🔹 US Stocks: 45%
            🔹 International: 25%
            🔹 Bonds: 20%
            🔹 Real Estate: 7%
            🔹 Cash: 3%

            Projected Returns:
            📈 Conservative: 6.2%
            📈 Moderate: 8.5%
            📈 Aggressive: 10.8%

            Smart Recommendations:
            💡 Increase tech sector exposure
            💡 Consider ESG investments
            💡 Rebalance quarterly
          `);
          setIsLoading(false);
        }, 3000);
      }
    },
    {
      id: 'decisions',
      icon: <Brain className="w-8 h-8 text-purple-600" />,
      title: "AI-Driven Decision Support",
      description: "Leverage advanced machine learning algorithms to make informed financial decisions with confidence.",
      color: "bg-purple-50/50",
      hoverColor: "hover:bg-purple-50",
      textColor: "text-purple-600",
      borderColor: "border-purple-100",
      demoPrompt: "Analyze your financial decisions?",
      demoAction: () => {
        setIsLoading(true);
        setTimeout(() => {
          setDemoResponse(`
            🤖 AI Decision Analysis

            Current Financial Health: Strong
            Monthly Savings: On Track
            Debt Management: Excellent

            Recent Decisions Analyzed:
            ✅ Home Purchase Planning
            ✅ Investment Strategy
            ✅ Emergency Fund Setup

            AI Recommendations:
            1️⃣ Optimal Mortgage Rate: 3.75%
            2️⃣ Suggested Down Payment: $60,000
            3️⃣ Emergency Fund Target: $25,000

            Action Items:
            🎯 Start house hunting in 3 months
            🎯 Increase 401(k) contribution by 2%
            🎯 Review insurance coverage
          `);
          setIsLoading(false);
        }, 2800);
      }
    },
    {
      id: 'budget',
      icon: <Calculator className="w-8 h-8 text-rose-600" />,
      title: "Automated Budget Optimization",
      description: "Let our AI analyze your spending habits and automatically suggest optimizations to help you save more.",
      color: "bg-rose-50/50",
      hoverColor: "hover:bg-rose-50",
      textColor: "text-rose-600",
      borderColor: "border-rose-100",
      demoPrompt: "Optimize your budget with AI?",
      demoAction: () => {
        setIsLoading(true);
        setTimeout(() => {
          setDemoResponse(`
            💰 Budget Optimization Report

            Monthly Overview:
            📥 Income: $6,500
            📤 Expenses: $4,800
            💫 Savings: $1,700

            Category Analysis:
            🏠 Housing: $2,000 (On target)
            🚗 Transport: $400 (High)
            🍽️ Food: $800 (High)
            🎮 Entertainment: $300 (Good)
            
            AI-Suggested Savings:
            🚗 Carpool to work: +$150/mo
            🍽️ Meal prep plan: +$200/mo
            📱 Switch carriers: +$45/mo
            
            Potential Monthly Savings: $395
            Annual Impact: $4,740 extra savings!
          `);
          setIsLoading(false);
        }, 2600);
      }
    },
    {
      id: 'notifications',
      icon: <Bell className="w-8 h-8 text-yellow-600" />,
      title: "Smart Alert System",
      description: "Stay informed with intelligent notifications about your finances, market changes, and investment opportunities.",
      color: "bg-yellow-50/50",
      hoverColor: "hover:bg-yellow-50",
      textColor: "text-yellow-600",
      borderColor: "border-yellow-100",
      demoPrompt: "Test the alert system?",
      demoAction: () => {
        setIsLoading(true);
        playNotificationSound();
        setTimeout(() => {
          setDemoResponse(`
            🔔 Alert System Test Results

            Notification Channels:
            📱 Mobile Push: Success
            ✉️ Email: Success
            💬 SMS: Success
            🔊 Sound Alert: Active

            Sample Alerts:
            💳 Large Transaction: $1,000+
            📈 Stock Alert: AAPL +5%
            💰 Low Balance: Below $500
            🏦 Bill Due: 3 days notice

            Custom Preferences Set:
            - Market updates: Daily
            - Balance alerts: Real-time
            - Bill reminders: 3 days prior
            
            Click below to test sound again!
          `);
          setIsLoading(false);
        }, 2000);
      }
    },
    {
      id: 'cloud',
      icon: <Cloud className="w-8 h-8 text-sky-600" />,
      title: "Cloud Sync & Analysis",
      description: "Securely upload and analyze your financial documents for deeper insights and personalized recommendations.",
      color: "bg-sky-50/50",
      hoverColor: "hover:bg-sky-50",
      textColor: "text-sky-600",
      borderColor: "border-sky-100",
      demoPrompt: "Start cloud analysis demo?",
      demoAction: () => {
        setIsLoading(true);
        setTimeout(() => {
          setDemoResponse(`
            ☁️ Cloud Analysis Demo

            Simulated Upload:
            📄 bank_statement.csv ✅
            📄 investments.xlsx ✅
            📄 budget_2024.pdf ✅

            AI Analysis Results:
            📊 3 months of transactions processed
            💡 15 spending patterns identified
            🎯 8 optimization opportunities found

            Key Insights:
            💰 Average monthly savings: $850
            📈 Investment returns: +12% YTD
            ⚡ Recurring expenses: $2,300/mo

            Recommendations:
            1. Consolidate subscriptions
            2. Increase emergency fund
            3. Review investment allocation
          `);
          setIsLoading(false);
        }, 2700);
      }
    },
    {
      id: 'team',
      icon: <Users className="w-8 h-8 text-indigo-600" />,
      title: "Team Collaboration",
      description: "Work together with your financial advisors and team members in a secure, collaborative environment.",
      color: "bg-indigo-50/50",
      hoverColor: "hover:bg-indigo-50",
      textColor: "text-indigo-600",
      borderColor: "border-indigo-100",
      demoPrompt: "Create a demo workspace?",
      demoAction: () => {
        setIsLoading(true);
        setTimeout(() => {
          setDemoResponse(`
            👥 Team Workspace Created!

            Workspace Details:
            🆔 ID: WS-${Math.random().toString(36).substr(2, 6).toUpperCase()}
            🔐 Security: End-to-end encrypted
            👤 Capacity: 5 team members

            Team Roles Configured:
            👨‍💼 Financial Advisor
            📊 Data Analyst
            💰 Investment Manager
            📋 Tax Specialist

            Features Enabled:
            💬 Secure Chat
            📁 Document Sharing
            📅 Meeting Scheduler
            📊 Real-time Dashboard

            Ready for team invites!
          `);
          setIsLoading(false);
        }, 2400);
      }
    },
    {
      id: 'automation',
      icon: <Clock className="w-8 h-8 text-teal-600" />,
      title: "Smart Automation",
      description: "Automate your financial tasks and recurring transactions with intelligent scheduling and monitoring.",
      color: "bg-teal-50/50",
      hoverColor: "hover:bg-teal-50",
      textColor: "text-teal-600",
      borderColor: "border-teal-100",
      demoPrompt: "Test automation features?",
      demoAction: () => {
        setIsLoading(true);
        setTimeout(() => {
          setDemoResponse(`
            ⚡ Automation System Active

            Scheduled Tasks:
            💰 Monthly Savings Transfer
            📅 Date: 1st of each month
            💵 Amount: $1,000
            ✅ Status: Configured

            Bill Payments:
            🏠 Rent: $2,200 (1st)
            📱 Phone: $85 (15th)
            🔌 Utilities: ~$150 (22nd)

            Investment Rules:
            📈 Auto-rebalance when 5% off
            💰 Dividend reinvestment: ON
            🎯 Tax-loss harvesting: ON

            Next Actions:
            ⏰ Rent payment in 3 days
            ⏰ Portfolio review in 1 week
            ⏰ Tax estimate in 2 weeks
          `);
          setIsLoading(false);
        }, 2200);
      }
    },
    {
      id: 'market',
      icon: <TrendingUp className="w-8 h-8 text-orange-600" />,
      title: "Real-Time Market Insights",
      description: "Get instant updates on market trends, cryptocurrency movements, and stock performance with AI-powered analysis.",
      color: "bg-orange-50/50",
      hoverColor: "hover:bg-orange-50",
      textColor: "text-orange-600",
      borderColor: "border-orange-100",
      demoPrompt: "View live market demo?",
      demoAction: () => {
        setIsLoading(true);
        setTimeout(() => {
          setDemoResponse(`
            📈 Live Market Demo

            Market Snapshot:
            S&P 500: 4,783.45 (+0.8%)
            NASDAQ: 15,123.45 (+1.2%)
            BTC/USD: $43,256.78 (+2.1%)

            Top Movers:
            🔼 TSLA: +4.2%
            🔼 AAPL: +2.8%
            🔽 META: -1.2%

            Sector Performance:
            💻 Technology: +2.3%
            🏥 Healthcare: +1.7%
            🏦 Financials: +0.9%

            AI Market Prediction:
            📊 Trend: Bullish
            ⚡ Volatility: Moderate
            💡 Opportunity: Tech Sector
          `);
          setIsLoading(false);
        }, 2500);
      }
    }
  ];

  const handleDemoClick = (feature: any) => {
    setShowDemo(feature.id);
    feature.demoAction();
  };

  return (
    <div className="w-full min-h-screen bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
        <h1 className="text-4xl sm:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
          Features
        </h1>
        <div className="w-full">
          <div className="w-full">
            <div className="mb-20 w-full px-4 sm:px-6 lg:px-8">
              <BankingDemo />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 lg:p-8 mb-20 mx-4 sm:mx-6 lg:mx-8"
            >
              <h3 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
                Meet Pocket: Your Real-Time Financial Guardian
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div className="mb-4 p-2 bg-[#68A511]/10 rounded-lg w-fit">
                    <ShoppingCart className="w-6 h-6 text-[#68A511]" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Smart Purchase Detection</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Real-time monitoring of shopping behavior to prevent impulsive purchases
                  </p>
                </div>

                <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div className="mb-4 p-2 bg-[#68A511]/10 rounded-lg w-fit">
                    <AlertTriangle className="w-6 h-6 text-[#68A511]" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Risk Assessment</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    AI-powered analysis of your financial health before major purchases
                  </p>
                </div>

                <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <div className="mb-4 p-2 bg-[#68A511]/10 rounded-lg w-fit">
                    <TrendingUp className="w-6 h-6 text-[#68A511]" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Smart Alternatives</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Get investment suggestions instead of impulsive purchases
                  </p>
                </div>
              </div>

              <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#68A511]/10 rounded-lg">
                    <DollarSign className="w-6 h-6 text-[#68A511]" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold mb-2">Smart Purchase Alert</h5>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      "I noticed you're about to spend $299 on new headphones. 
                      Based on your current budget and upcoming bills, this might stretch your finances. 
                      Would you like to see some alternative options?"
                    </p>
                    <div className="flex gap-3">
                      <button className="px-4 py-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e] transition-colors">
                        Show Alternatives
                      </button>
                      <button className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors">
                        Continue Purchase
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="flex flex-col w-full">
              <div className="flex-grow w-full">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                  <motion.div
                    initial="initial"
                    animate="animate"
                    className="text-center mb-20"
                  >
                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-4xl md:text-5xl font-bold text-[#68A511] mb-4"
                    >
                      Transform Your Financial Future
                    </motion.h1>
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-lg text-emerald-600/80 dark:text-emerald-500/80 max-w-3xl mx-auto"
                    >
                      Experience the power of AI-driven financial management with our innovative features
                    </motion.p>
                  </motion.div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8 mb-32 w-full">
                    {features.map((feature, i) => (
                      <motion.div
                        key={feature.id}
                        custom={i}
                        initial="hidden"
                        animate="visible"
                        whileHover="hover"
                        whileTap="tap"
                        variants={cardVariants}
                        className={`${feature.color} rounded-xl p-6 border ${feature.borderColor} ${feature.hoverColor} transition-all duration-200 cursor-pointer backdrop-blur-sm relative overflow-hidden group`}
                      >
                        {/* Animated background gradient */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                          initial={{ x: '-100%' }}
                          whileHover={{ x: '100%' }}
                          transition={{ duration: 0.8, ease: "easeInOut" }}
                        />

                        <motion.div
                          variants={iconVariants}
                          className={`${feature.textColor} mb-4 relative`}
                        >
                          {feature.icon}
                          {/* Pulse effect behind icon */}
                          <div className="absolute inset-0 animate-ping opacity-20 rounded-full bg-current" />
                        </motion.div>

                        <motion.h3
                          className={`text-xl font-semibold mb-3 ${feature.textColor} relative z-10`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.2 }}
                        >
                          {feature.title}
                        </motion.h3>

                        <motion.p
                          className="text-gray-600 dark:text-gray-300 mb-6 relative z-10"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: i * 0.2 + 0.1 }}
                        >
                          {feature.description}
                        </motion.p>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDemoClick(feature)}
                          className="w-full py-2 bg-gray-100/80 hover:bg-gray-200/80 rounded-lg text-gray-700 font-medium transition-colors duration-200 relative z-10 group-hover:bg-opacity-100"
                        >
                          <span className="relative inline-flex items-center">
                            Try This Feature
                            <motion.span
                              className="ml-2"
                              initial={{ x: -5, opacity: 0 }}
                              whileHover={{ x: 0, opacity: 1 }}
                            >
                              →
                            </motion.span>
                          </span>
                        </motion.button>
                      </motion.div>
                    ))}
                  </div>

                  {/* Financial Overview Graph Section - Now full width */}
                  <div className="w-full space-y-16 mb-32">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 lg:p-8"
                    >
                      <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                          Financial Growth Overview
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 mb-8">
                          Track your financial progress with our AI-powered insights
                        </p>
                      </div>

                      <div className="h-[400px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={spendingData}>
                            <defs>
                              <linearGradient id="expenses" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.1}/>
                                <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="savings" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="investments" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                                <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                            <XAxis 
                              dataKey="month" 
                              stroke="#6b7280"
                              fontSize={12}
                            />
                            <YAxis 
                              stroke="#6b7280"
                              fontSize={12}
                              tickFormatter={(value) => `$${value}`}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: '8px',
                                border: 'none',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                              }}
                              formatter={(value) => [`$${value}`, '']}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="expenses" 
                              stroke="#ef4444" 
                              fillOpacity={1} 
                              fill="url(#expenses)"
                              strokeWidth={2}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="savings" 
                              stroke="#10b981" 
                              fillOpacity={1} 
                              fill="url(#savings)"
                              strokeWidth={2}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="investments" 
                              stroke="#6366f1" 
                              fillOpacity={1} 
                              fill="url(#investments)"
                              strokeWidth={2}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>

                      <div className="flex justify-center gap-8 mt-8">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">Expenses</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">Savings</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                          <span className="text-gray-600 dark:text-gray-300">Investments</span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Enhanced Footer Styling */}
              <footer className="w-full bg-transparent [&_button]:bg-[#68A511] [&_button]:hover:bg-[#527f0e] mt-20">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
                  <div className="max-w-[1920px] mx-auto w-full">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          About Green Guard
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                          Empowering your financial journey with AI-driven insights and intelligent money management solutions.
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          Quick Links
                        </h3>
                        <ul className="space-y-2">
                          <li>
                            <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-500 transition-colors">
                              Features
                            </a>
                          </li>
                          <li>
                            <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-500 transition-colors">
                              Security
                            </a>
                          </li>
                          <li>
                            <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-500 transition-colors">
                              Pricing
                            </a>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-4">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                          Contact Us
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                          Have questions? Reach out to our support team anytime.
                        </p>
                        <button className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors">
                          Get Support
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                        <p className="text-gray-600 dark:text-gray-300 text-center md:text-left">
                          © 2024 Green Guard. All rights reserved.
                        </p>
                        <div className="flex space-x-6">
                          <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-500 transition-colors">
                            Privacy Policy
                          </a>
                          <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-500 transition-colors">
                            Terms of Service
                          </a>
                          <a href="#" className="text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-500 transition-colors">
                            Cookie Policy
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </footer>

              {/* Modal stays at the end */}
              <AnimatePresence>
                {showDemo && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    onClick={() => {
                      setShowDemo(null);
                      setDemoResponse('');
                    }}
                  >
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.95, opacity: 0 }}
                      className="bg-white/80 backdrop-blur-md dark:bg-gray-800/80 rounded-xl p-8 max-w-md w-full shadow-xl"
                      onClick={e => e.stopPropagation()}
                    >
                      {!demoResponse ? (
                        <motion.div
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          className="space-y-6"
                        >
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {features.find(f => f.id === showDemo)?.demoPrompt}
                          </h3>
                          <div className="flex justify-center gap-4">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => features.find(f => f.id === showDemo)?.demoAction()}
                              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                            >
                              Run Demo
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setShowDemo(null);
                                setDemoResponse('');
                              }}
                              className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                            >
                              Cancel
                            </motion.button>
                          </div>
                        </motion.div>
                      ) : (
                        <div className="space-y-4">
                          {isLoading ? (
                            <div className="space-y-6">
                              <div className="flex flex-col items-center justify-center py-8">
                                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 mb-4">
                                  <motion.div
                                    className="bg-green-600 h-2.5 rounded-full"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <p className="text-gray-600 dark:text-gray-300 animate-pulse">
                                  Processing... {progress}%
                                </p>
                              </div>
                            </div>
                          ) : (
                            <motion.div
                              initial={{ y: 20, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              className="relative"
                            >
                              {showSuccess && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  exit={{ scale: 0 }}
                                  className="absolute inset-0 flex items-center justify-center bg-green-500/20 rounded-lg backdrop-blur-sm"
                                >
                                  <Check className="w-16 h-16 text-green-500" />
                                </motion.div>
                              )}
                              <div className="flex justify-between items-center">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                  {features.find(f => f.id === showDemo)?.title}
                                </h3>
                                <motion.button
                                  whileHover={{ scale: 1.1, rotate: 90 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => {
                                    setShowDemo(null);
                                    setDemoResponse('');
                                  }}
                                  className="text-gray-500 hover:text-gray-700"
                                >
                                  ×
                                </motion.button>
                              </div>
                              <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                                {demoResponse}
                              </div>
                              {showDemo === 'notifications' && (
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => playNotificationSound()}
                                  className="w-full px-6 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition mt-4 flex items-center justify-center gap-2"
                                >
                                  <Bell className="w-4 h-4" />
                                  Replay Alert Sound
                                </motion.button>
                              )}
                            </motion.div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;